import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { AuthContext, type AuthError, type AuthStatus } from './context';
import { getAuthClient } from '../firebase';

/** Cancelling the Google window is a decision, not an error worth reporting. */
const SILENT_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

function errorFor(error: unknown): AuthError | null {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';

  if (SILENT_CODES.has(code)) return null;
  if (code === 'auth/popup-blocked') return { kind: 'popup-blocked', code };

  // Log the whole thing: the code alone is sometimes not enough, and this is
  // the only place the underlying failure is still visible.
  console.error('Sign-in failed', error);
  return { kind: 'failed', code: code || null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    return onAuthStateChanged(getAuthClient(), (next) => {
      setUser(next);
      setStatus(next ? 'signed-in' : 'signed-out');
    });
  }, []);

  const signIn = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      // Always ask which account — people keep more than one.
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(getAuthClient(), provider);
    } catch (cause) {
      setError(errorFor(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    await firebaseSignOut(getAuthClient());
  }, []);

  const value = useMemo(
    () => ({ status, user, busy, error, signIn, signOut }),
    [status, user, busy, error, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
